import IndexedDBWrapper from './indexeddb-wrapper.js';

/**
 * StorageManager handles all data persistence for the Beginner Interval Runner app
 * Manages workout progress, user preferences, and program data
 */
class StorageManager {
    constructor() {
        this.db = new IndexedDBWrapper('BeginnerIntervalRunner', 1);
        this.isInitialized = false;
    }

    /**
     * Initialize the storage system
     * @returns {Promise<void>}
     */
    async init() {
        if (this.isInitialized) return;

        const stores = [
            {
                name: 'workouts',
                options: { keyPath: 'id' },
                indexes: [
                    { name: 'programId', keyPath: 'programId' },
                    { name: 'completedAt', keyPath: 'completedAt' },
                    { name: 'completed', keyPath: 'completed' }
                ]
            },
            {
                name: 'programs',
                options: { keyPath: 'id' }
            },
            {
                name: 'userPreferences',
                options: { keyPath: 'key' }
            },
            {
                name: 'achievements',
                options: { keyPath: 'id' },
                indexes: [
                    { name: 'unlockedAt', keyPath: 'unlockedAt' }
                ]
            }
        ];

        await this.db.init(stores);
        this.isInitialized = true;
    }

    // Workout Progress Methods

    /**
     * Save a completed workout
     * @param {Object} workout - Completed workout data
     * @returns {Promise<void>}
     */
    async saveWorkout(workout) {
        const workoutData = {
            id: this._generateId(),
            programId: workout.programId,
            sessionId: workout.sessionId,
            completedAt: workout.completedAt || new Date().toISOString(),
            duration: workout.duration,
            completed: workout.completed || true,
            ...workout
        };

        await this.db.put('workouts', workoutData);
    }

    /**
     * Get all completed workouts
     * @returns {Promise<Array>}
     */
    async getWorkouts() {
        return await this.db.getAll('workouts');
    }

    /**
     * Get workouts for a specific program
     * @param {string} programId - Program identifier
     * @returns {Promise<Array>}
     */
    async getWorkoutsByProgram(programId) {
        return await this.db.queryByIndex('workouts', 'programId', programId);
    }

    /**
     * Get workouts within a date range
     * @param {Date} startDate - Start date
     * @param {Date} endDate - End date
     * @returns {Promise<Array>}
     */
    async getWorkoutsByDateRange(startDate, endDate) {
        const allWorkouts = await this.db.getAll('workouts');
        return allWorkouts.filter(workout => {
            const workoutDate = new Date(workout.completedAt);
            return workoutDate >= startDate && workoutDate <= endDate;
        });
    }

    /**
     * Delete a workout record
     * @param {string} workoutId - Workout identifier
     * @returns {Promise<void>}
     */
    async deleteWorkout(workoutId) {
        await this.db.delete('workouts', workoutId);
    }

    // User Preferences Methods

    /**
     * Save user preference
     * @param {string} key - Preference key
     * @param {any} value - Preference value
     * @returns {Promise<void>}
     */
    async setPreference(key, value) {
        await this.db.put('userPreferences', { key, value });
    }

    /**
     * Get user preference
     * @param {string} key - Preference key
     * @param {any} defaultValue - Default value if not found
     * @returns {Promise<any>}
     */
    async getPreference(key, defaultValue = null) {
        const result = await this.db.get('userPreferences', key);
        return result ? result.value : defaultValue;
    }

    /**
     * Get all user preferences
     * @returns {Promise<Object>}
     */
    async getAllPreferences() {
        const preferences = await this.db.getAll('userPreferences');
        const result = {};
        preferences.forEach(pref => {
            result[pref.key] = pref.value;
        });
        return result;
    }

    /**
     * Delete a user preference
     * @param {string} key - Preference key
     * @returns {Promise<void>}
     */
    async deletePreference(key) {
        await this.db.delete('userPreferences', key);
    }

    // Program Data Methods

    /**
     * Save program data
     * @param {Object} program - Program data
     * @returns {Promise<void>}
     */
    async saveProgram(program) {
        await this.db.put('programs', program);
    }

    /**
     * Get program by ID
     * @param {string} programId - Program identifier
     * @returns {Promise<Object|null>}
     */
    async getProgram(programId) {
        return await this.db.get('programs', programId);
    }

    /**
     * Get all programs
     * @returns {Promise<Array>}
     */
    async getPrograms() {
        return await this.db.getAll('programs');
    }

    /**
     * Delete a program
     * @param {string} programId - Program identifier
     * @returns {Promise<void>}
     */
    async deleteProgram(programId) {
        await this.db.delete('programs', programId);
    }

    // Achievement Methods

    /**
     * Save achievement
     * @param {Object} achievement - Achievement data
     * @returns {Promise<void>}
     */
    async saveAchievement(achievement) {
        const achievementData = {
            id: achievement.id,
            name: achievement.name,
            description: achievement.description,
            unlockedAt: achievement.unlockedAt || new Date().toISOString(),
            ...achievement
        };

        await this.db.put('achievements', achievementData);
    }

    /**
     * Get all achievements
     * @returns {Promise<Array>}
     */
    async getAchievements() {
        return await this.db.getAll('achievements');
    }

    /**
     * Check if achievement is unlocked
     * @param {string} achievementId - Achievement identifier
     * @returns {Promise<boolean>}
     */
    async hasAchievement(achievementId) {
        const achievement = await this.db.get('achievements', achievementId);
        return !!achievement;
    }

    // Utility Methods

    /**
     * Clear all data (for testing or reset)
     * @returns {Promise<void>}
     */
    async clearAllData() {
        await Promise.all([
            this.db.clear('workouts'),
            this.db.clear('programs'),
            this.db.clear('userPreferences'),
            this.db.clear('achievements')
        ]);
    }

    /**
     * Get storage usage statistics
     * @returns {Promise<Object>}
     */
    async getStorageStats() {
        const [workoutCount, programCount, preferenceCount, achievementCount] = await Promise.all([
            this.db.count('workouts'),
            this.db.count('programs'),
            this.db.count('userPreferences'),
            this.db.count('achievements')
        ]);

        return {
            workouts: workoutCount,
            programs: programCount,
            preferences: preferenceCount,
            achievements: achievementCount
        };
    }

    /**
     * Export all data for backup
     * @returns {Promise<Object>}
     */
    async exportData() {
        const [workouts, programs, preferences, achievements] = await Promise.all([
            this.db.getAll('workouts'),
            this.db.getAll('programs'),
            this.db.getAll('userPreferences'),
            this.db.getAll('achievements')
        ]);

        return {
            workouts,
            programs,
            preferences,
            achievements,
            exportedAt: new Date().toISOString()
        };
    }

    /**
     * Import data from backup
     * @param {Object} data - Exported data
     * @returns {Promise<void>}
     */
    async importData(data) {
        if (data.workouts) {
            for (const workout of data.workouts) {
                await this.db.put('workouts', workout);
            }
        }

        if (data.programs) {
            for (const program of data.programs) {
                await this.db.put('programs', program);
            }
        }

        if (data.preferences) {
            for (const preference of data.preferences) {
                await this.db.put('userPreferences', preference);
            }
        }

        if (data.achievements) {
            for (const achievement of data.achievements) {
                await this.db.put('achievements', achievement);
            }
        }
    }

    /**
     * Generate a unique ID
     * @private
     */
    _generateId() {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Close the database connection
     */
    close() {
        this.db.close();
        this.isInitialized = false;
    }
}

export default StorageManager;